import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronRight, X } from 'lucide-react'
import { hasSeenFeatureTour, markFeatureTourSeen } from './FeatureAnnouncementModal'
import './ProductTour.css'

const PAD = 8
const CARD_WIDTH = 320
const CARD_FALLBACK_HEIGHT = 208
const GAP = 14
const MARGIN = 12

function wait(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms))
}

function readRect(selector) {
  if (!selector || typeof document === 'undefined') return null
  const el = document.querySelector(selector)
  if (!el) return null
  const r = el.getBoundingClientRect()
  if (r.width < 2 && r.height < 2) return null
  return {
    top: Math.max(8, r.top - PAD),
    left: Math.max(8, r.left - PAD),
    width: r.width + PAD * 2,
    height: r.height + PAD * 2,
    bottom: Math.max(8, r.top - PAD) + r.height + PAD * 2,
    right: Math.max(8, r.left - PAD) + r.width + PAD * 2,
  }
}

function rectsOverlap(a, b, pad = 8) {
  return !(
    a.left + a.width + pad < b.left ||
    a.left > b.right + pad ||
    a.top + a.height + pad < b.top ||
    a.top > b.bottom + pad
  )
}

function placeCard(hole, cardW, cardH, preferred = 'auto') {
  const vw = window.innerWidth
  const vh = window.innerHeight

  if (vw < 768) {
    return {
      top: vh - cardH - 16,
      left: (vw - Math.min(cardW, vw - 24)) / 2,
      width: Math.min(cardW, vw - 24),
      side: 'dock',
    }
  }

  const fits = {
    bottom: hole.bottom + GAP + cardH <= vh - MARGIN,
    top: hole.top - GAP - cardH >= MARGIN,
    right: hole.right + GAP + cardW <= vw - MARGIN,
    left: hole.left - GAP - cardW >= MARGIN,
  }

  let side = preferred
  if (side === 'auto' || !fits[side]) {
    side = ['bottom', 'top', 'right', 'left'].find((key) => fits[key]) || 'dock'
  }

  let top
  let left
  if (side === 'bottom') {
    top = hole.bottom + GAP
    left = hole.left + hole.width / 2 - cardW / 2
  } else if (side === 'top') {
    top = hole.top - GAP - cardH
    left = hole.left + hole.width / 2 - cardW / 2
  } else if (side === 'right') {
    left = hole.right + GAP
    top = hole.top + hole.height / 2 - cardH / 2
  } else if (side === 'left') {
    left = hole.left - GAP - cardW
    top = hole.top + hole.height / 2 - cardH / 2
  } else {
    return {
      top: vh - cardH - 16,
      left: vw - cardW - 16,
      width: cardW,
      side: 'dock',
    }
  }

  left = Math.min(Math.max(MARGIN, left), vw - cardW - MARGIN)
  top = Math.min(Math.max(MARGIN, top), vh - cardH - MARGIN)

  const cardBox = { top, left, width: cardW, height: cardH }
  if (rectsOverlap(cardBox, hole)) {
    return {
      top: vh - cardH - 16,
      left: vw - cardW - 16,
      width: cardW,
      side: 'dock',
    }
  }

  return { top, left, width: cardW, side }
}

function arrowStyle(side, hole, card) {
  if (!hole || side === 'dock') return { display: 'none' }
  if (side === 'bottom' || side === 'top') {
    const x = Math.min(
      Math.max(18, hole.left + hole.width / 2 - card.left - 6),
      card.width - 24
    )
    return { left: x }
  }
  const y = Math.min(
    Math.max(18, hole.top + hole.height / 2 - card.top - 6),
    72
  )
  return { top: y }
}

/**
 * Compact live coachmark tour. Highlights real UI, keeps copy beside it.
 */
const ProductTour = ({
  tourId,
  steps = [],
  isOpen,
  onClose,
  onStart,
  onStepChange,
}) => {
  const titleId = useId()
  const reduceMotion = useReducedMotion()
  const cardRef = useRef(null)
  const nextRef = useRef(null)
  const cancelRef = useRef(0)
  const startedRef = useRef(false)

  const [ready, setReady] = useState(false)
  const [active, setActive] = useState(false)
  const [index, setIndex] = useState(0)
  const [hole, setHole] = useState(null)
  const [cardSize, setCardSize] = useState({ width: CARD_WIDTH, height: CARD_FALLBACK_HEIGHT })

  const step = steps[index] || null
  const isLast = index >= steps.length - 1
  const spring = reduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 280, damping: 28 }

  const finish = useCallback(async () => {
    setActive(false)
    setHole(null)
    markFeatureTourSeen(tourId)
    try {
      await onClose?.({ tourId, seen: true })
    } catch {
      /* ignore */
    }
  }, [onClose, tourId])

  const goTo = useCallback(
    (nextIndex) => {
      setIndex(nextIndex)
      onStepChange?.(nextIndex, steps[nextIndex])
    },
    [onStepChange, steps]
  )

  useEffect(() => {
    if (!tourId || !steps.length) {
      setReady(true)
      return
    }
    if (isOpen === false) {
      setActive(false)
      setReady(true)
      return
    }
    const seen = hasSeenFeatureTour(tourId)
    const shouldShow = isOpen === true || !seen
    if (shouldShow) {
      setIndex(0)
      setActive(true)
    } else {
      setActive(false)
    }
    setReady(true)
  }, [tourId, isOpen, steps.length])

  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true
      onStart?.()
    }
    if (!active) startedRef.current = false
  }, [active, onStart])

  useEffect(() => {
    if (!active || !step?.target) {
      setHole(null)
      return undefined
    }

    const token = ++cancelRef.current
    let stopped = false

    const run = async () => {
      let tries = 0
      while (!document.querySelector(step.target) && tries < 40 && !stopped) {
        tries += 1
        await wait(80)
      }
      if (stopped || token !== cancelRef.current) return

      const el = document.querySelector(step.target)
      if (!el) return
      el.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      })
      await wait(reduceMotion ? 40 : 220)
      if (stopped || token !== cancelRef.current) return

      const next = readRect(step.target)
      if (next) setHole(next)
    }

    run()

    const sync = () => {
      if (stopped || token !== cancelRef.current) return
      const next = readRect(step.target)
      if (next) setHole(next)
    }
    window.addEventListener('resize', sync)
    window.addEventListener('scroll', sync, true)

    return () => {
      stopped = true
      window.removeEventListener('resize', sync)
      window.removeEventListener('scroll', sync, true)
    }
  }, [active, step, reduceMotion])

  useLayoutEffect(() => {
    if (!active || !cardRef.current) return
    const rect = cardRef.current.getBoundingClientRect()
    if (rect.width < 2 || rect.height < 2) return
    setCardSize((prev) => {
      if (Math.abs(prev.width - rect.width) < 1 && Math.abs(prev.height - rect.height) < 1) {
        return prev
      }
      return { width: rect.width, height: rect.height }
    })
  }, [active, index, hole, step])

  useEffect(() => {
    if (!active) return undefined
    const onKeyDown = (event) => {
      if (event.key === 'Escape') finish()
      if (event.key === 'ArrowRight' && index < steps.length - 1) goTo(index + 1)
      if (event.key === 'ArrowLeft' && index > 0) goTo(index - 1)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [active, finish, goTo, index, steps.length])

  useEffect(() => {
    if (active) nextRef.current?.focus()
  }, [active, index])

  if (!ready || !active || typeof document === 'undefined') return null

  const cardPos = hole
    ? placeCard(hole, cardSize.width, cardSize.height, step?.placement)
    : { top: 24, left: 24, width: CARD_WIDTH, side: 'dock' }
  const arrow = hole ? arrowStyle(cardPos.side, hole, { ...cardPos, width: cardSize.width }) : { display: 'none' }

  return createPortal(
    <AnimatePresence>
      {active ? (
        <div className="product-tour-root" role="dialog" aria-modal="true" aria-labelledby={titleId}>
          {hole ? (
            <>
              <div
                className="pointer-events-auto absolute left-0 right-0 top-0 z-[81]"
                style={{ height: Math.max(0, hole.top) }}
              />
              <div
                className="pointer-events-auto absolute left-0 z-[81]"
                style={{ top: hole.top, width: Math.max(0, hole.left), height: hole.height }}
              />
              <div
                className="pointer-events-auto absolute right-0 z-[81]"
                style={{
                  top: hole.top,
                  width: Math.max(0, window.innerWidth - hole.right),
                  height: hole.height,
                }}
              />
              <div
                className="pointer-events-auto absolute bottom-0 left-0 right-0 z-[81]"
                style={{ height: Math.max(0, window.innerHeight - hole.bottom) }}
              />
              <motion.div
                className="product-tour-spotlight"
                initial={false}
                animate={{
                  top: hole.top,
                  left: hole.left,
                  width: hole.width,
                  height: hole.height,
                }}
                transition={spring}
              />
            </>
          ) : (
            <div className="pointer-events-auto absolute inset-0 bg-slate-900/40" />
          )}

          <motion.div
            ref={cardRef}
            key={step?.target || index}
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            className="product-tour-card"
            style={{
              top: cardPos.top,
              left: cardPos.left,
              width: cardPos.width,
            }}
          >
            {cardPos.side !== 'dock' ? (
              <span className="product-tour-arrow" data-side={cardPos.side} style={arrow} />
            ) : null}

            <div className="relative px-4 pb-4 pt-3.5">
              <div className="flex items-start justify-between gap-3">
                <p className="text-[11px] font-semibold tabular-nums text-slate-400">
                  {index + 1} of {steps.length}
                </p>
                <button
                  type="button"
                  onClick={finish}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-50 hover:text-slate-700"
                  aria-label="Skip tour"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <h2 id={titleId} className="pr-2 text-base font-semibold leading-snug text-slate-900">
                {step?.title}
              </h2>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-500">
                {step?.description}
              </p>

              <div className="mt-4 flex items-center gap-3">
                <div className="flex items-center gap-1" aria-hidden>
                  {steps.map((item, dotIndex) => (
                    <span
                      key={item.target}
                      className={`product-tour-dot ${dotIndex === index ? 'is-active' : ''}`}
                    />
                  ))}
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  {index > 0 ? (
                    <button
                      type="button"
                      onClick={() => goTo(index - 1)}
                      className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      Back
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={finish}
                      className="inline-flex h-9 items-center rounded-lg px-3 text-sm font-semibold text-slate-500 hover:bg-slate-50"
                    >
                      Skip
                    </button>
                  )}
                  <button
                    ref={nextRef}
                    type="button"
                    onClick={() => (isLast ? finish() : goTo(index + 1))}
                    className="inline-flex h-9 items-center gap-1 rounded-lg bg-[#263f97] px-3.5 text-sm font-semibold text-white hover:bg-[#1e327a]"
                  >
                    {isLast ? 'Done' : 'Next'}
                    {isLast ? null : <ChevronRight className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>,
    document.body
  )
}

ProductTour.propTypes = {
  tourId: PropTypes.string.isRequired,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      target: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      placement: PropTypes.oneOf(['auto', 'top', 'bottom', 'left', 'right']),
    })
  ),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onStart: PropTypes.func,
  onStepChange: PropTypes.func,
}

export default ProductTour
